<script setup lang="ts">
import { calculatePercentage } from '@/utils';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import CollectivitesCartesTableComponent from './@components/CollectivitesCartesTableComponent.vue';
import ConfirmationModal from '@/components/ConfirmationModal.vue';
import axios from 'axios';

const BACKEND_URL = inject('BACKEND_URL');
const route = useRoute();
const startup_infos: any = (route.meta as any).item;
const collectivite = ref<any>((route.meta as any).item.collectivite || {});
const stats = ref<{
    total: number,
    active: number,
    delivered: number,
    delivering: number,
    disabled: number,
    inactive: number,
    pending: number,
    withdrawn: number
}>((route.meta as any).item.stats || {
    total: 0,
    active: 0,
    delivered: 0,
    delivering: 0,
    disabled: 0,
    inactive: 0,
    pending: 0,
    withdrawn: 0
});
//const users = ref<any[]>((route.meta as any).item.users);

const membersPercentage = computed(() => {
    const maxMembers = 50000; // Valeur par défaut pour la barre de progression
    return Math.min(100, calculatePercentage(collectivite.value.users_count, maxMembers));
});
</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Collectivité / Cartes <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <!-- light2 border-none -->
            <span class="btn shadow-200 white2">Collectivités</span>
            <span class="btn shadow-200 white2">Statistiques</span>
            <span class="btn shadow-200 white2">Gérer les cartes</span>
            <span class="btn shadow-200 white2">Prélèvements / Décaissements</span>
        </div>


        <!--Collectivité info - Component-->
        <div class="s20 widget flex align-items-center px-1 py-5">
            <div class="s2_mobile h-100p px-7">
                <div class="rounded-100 overflow-hidden item active"><!--active-->
                    <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/assets/logos/logo2.svg'" width="100%" />
                </div>
            </div>
            <div class="s4_mobile h-100p px-2">
                <h4 class="mb-2">{{ collectivite.nom || '' }}</h4>
                <h3>{{ collectivite.rccm || 'N/A' }}</h3>
            </div>
            <div class="s3_mobile h-100p px-2">
                <h4 class="font-normal mb-5">{{ collectivite.users_count || 0 }} / 50000 Membres</h4>
                <span class="tag-state warning stroke">En cours</span>
                <span class="tag-state notif stroke">Actif</span>
            </div>
            <div class="s4_mobile h-100p px-3">
                <h4 class="font-normal">
                    Date de souscription <br class="mb-2"/>
                    <b class="text-2xl">11 Janvier 2028</b>
                </h4>
            </div>
            <div class="s7_mobile h-100p px-5">
                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">Membres {{ collectivite.users_count ? Math.round((collectivite.users_count / 50000) * 100) : 0 }}%</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{ width: membersPercentage + '%' }">
                        {{ collectivite.users_count || 0 }} / 50000
                    </div>
                </div>
                <!--End Barre de progression-->
            </div>
        </div>
        <!--End Collectivité info-->
        <div class="clear-both h-20"></div>
        
        <!--Gestion des cartes - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Gestion des cartes
                </span>
            </div>
            <!-- <div class="s10 mt--5-5 flex-right">
                <button class="btn lg py-3 white2 active">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Cartes</span>
                </button>
            </div> -->

            <div class="s20 gap-6 px-5 pb-8 collapse-content active">
                <div class="clear-both h-10"></div>
                <div class="flex items-center gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-black"></div>
                            Cartes créées
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ (stats.total || 0) + '/' + (collectivite.users_count || 0) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">92%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-black" style="width: 92%;">
                                92%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Cartes en livraison
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ (stats.delivering || 0) + '/' + (collectivite.users_count || 0) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">56.4%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" style="width: 56.4%;">
                                56.4%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Cartes livrées
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ (stats.delivered || 0) + '/' + (collectivite.users_count || 0) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Cartes retirées
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ (stats.withdrawn || 0) + '/' + (collectivite.users_count || 0) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Cartes activées
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">{{ (stats.active || 0) + '/' + (collectivite.users_count || 0) }}</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                </div>
            </div>

        </div>
        <!--End Gestion des cartes -->

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Gérer les cartes-->
        <CollectivitesCartesTableComponent :collectivite="collectivite"></CollectivitesCartesTableComponent>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Prélèvements / Décaissements-->
        <FinancePrelevementsDecaissements :startup-infos="startup_infos" />

        <div class="clear-both h-50"></div>

        <confirmation-modal ref="confirmationModalRef" />
    </section>
</template>